import {ResourcesConfig} from "aws-amplify"

// Helper function to get the correct RP ID based on the current hostname
const getRelyingPartyId = (): string => {
  const hostname = window.location.hostname

  // IMPORTANT: If you configured "Third-party domain" with a specific domain in Cognito,
  // we might need to use that exact domain. Based on user feedback, dev pool might be
  // configured with "lucenthealth.com" as the third-party domain.

  // Check environment to determine which Cognito pool we're using
  const isDev = hostname.includes('dev-') || hostname === 'localhost' || hostname === '127.0.0.1'

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // For localhost with dev pool, if Cognito expects lucenthealth.com, we might need to use it
    console.log('🔐 WebAuthn RP ID: localhost detected')
    console.log('⚠️ Note: If dev Cognito pool is configured with specific domain, this might not work')
    console.log('💡 Try using lucenthealth.com if registration fails')
    return 'localhost'
  }

  // Lucent Health domains: *.lucenthealth.com → lucenthealth.com
  if (hostname.endsWith('lucenthealth.com')) {
    console.log('🔐 WebAuthn RP ID: Using "lucenthealth.com" for all Lucent Health subdomains')
    console.log('✅ Passkeys will work across all *.lucenthealth.com domains')
    return 'lucenthealth.com'
  }

  // Narus Health domains: *.narushealth.com → narushealth.com
  if (hostname.endsWith('narushealth.com')) {
    // If dev pool is configured with lucenthealth.com, we might need special handling
    if (isDev) {
      console.log('🔐 WebAuthn RP ID: Dev environment on narushealth.com')
      console.log('⚠️ If Cognito dev pool expects lucenthealth.com, registration might fail')
      console.log('💡 Consider using lucenthealth.com domain for testing')
    }
    console.log('🔐 WebAuthn RP ID: Using "narushealth.com" for all Narus Health subdomains')
    return 'narushealth.com'
  }

  // FALLBACK: Return hostname as-is
  console.warn(`⚠️ WebAuthn: Unexpected hostname ${hostname}, using as-is for RP ID`)
  return hostname
}

export const createAwsConfig = (): ResourcesConfig => {
  const relyingPartyId = getRelyingPartyId()
  console.log(`🔐 WebAuthn Config: hostname="${window.location.hostname}" → RP ID="${relyingPartyId}"`)

  // Get Cognito values with fallbacks
  const cognitoConfig = window.constants?.COGNITO || {}
  const userPoolId = cognitoConfig.poolId || ''
  const userPoolClientId = cognitoConfig.frontendClient?.id || ''
  const identityPoolId = (cognitoConfig as any).identityPoolId || undefined
  const domain = (cognitoConfig as any).domain || undefined

  // AWS Amplify v6 configuration structure
  const config: ResourcesConfig = {
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
        ...(identityPoolId && { identityPoolId }),
        allowGuestAccess: false
      }
    }
  }

  // Add loginWith only if we have the necessary OAuth config
  if (domain) {
    (config.Auth?.Cognito as any).loginWith = {
      oauth: {
        domain,
        scope: ['email', 'openid', 'profile'],
        redirectSignIn: window.location.origin,
        redirectSignOut: window.location.origin,
        responseType: 'code'
      },
      passkey: {
        relyingPartyName: 'Lucent Health',
        relyingPartyId,
        userVerification: 'preferred'
      }
    }
  } else {
    // If no OAuth domain, just add passkey config
    (config.Auth?.Cognito as any).loginWith = {
      passkey: {
        relyingPartyName: 'Lucent Health',
        relyingPartyId,
        userVerification: 'preferred'
      }
    }
  }

  return config
}
